import { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { useQueries } from '@tanstack/react-query';
import { Id } from '@hero/schema';
import { useSearch } from '@hero/hooks-shared';
import {
  toggleSelectionItem,
  replaceSelectionItem,
  normalizeSelectionValue,
  defaultGetItemId,
} from './utils';
import { UseSelectAsyncOptions } from './types';

/**
 * Hook for async select with listQuery (raw data) and singleQuery (runQuery to resolve selected ids).
 * Resolves value (ids) to items internally via singleQuery.runQuery + useQueries.
 */
export function useSelectAsync<T extends { _id: Id } = any>(hookOptions: UseSelectAsyncOptions<T>) {
  const {
    mode = 'single',
    value,
    onChangeSelect,
    listQuery,
    singleQuery,
    getItemId: getItemIdProp,
    filterSelectionToCurrentList = false,
    searchDebounceMs = 300,
  } = hookOptions;

  const getItemId = getItemIdProp ?? defaultGetItemId;

  const { ids } = useMemo(
    () => normalizeSelectionValue(value as Parameters<typeof normalizeSelectionValue>[0]),
    [value],
  );

  const [open, setOpenState] = useState(false);

  const runQuerySearch = useCallback((search: string) => {
    listQuery.runQuery({ search });
  }, []);

  const search = useSearch({
    delay: searchDebounceMs,
    onSearchReadyChange: runQuerySearch,
  });

  const setOpen = useCallback((next: boolean) => {
    setOpenState(next);
    if (!next) search.clearSearch();
  }, []);

  const loadMoreRef = useRef<HTMLDivElement>(null);
  const listContainerRef = useRef<HTMLDivElement>(null);
  const [listRefReady, setListRefReady] = useState(false);

  const lastAddRef = useRef<{ id: Id; timestamp: number } | null>(null);
  const lastRemoveRef = useRef<{ id: Id; timestamp: number } | null>(null);
  const DEDUPE_MS = 150;

  const options = listQuery.data;
  const { onLoadMore, hasNextPage, isFetchingNextPage, isLoading } = listQuery;

  // Which selected ids are already in the current list (no API call needed)?
  const idsInList = useMemo(
    () => ids.filter(id => options.some(o => getItemId(o).toString() === id.toString())),
    [ids, options, getItemId],
  );
  const idsNotInList = useMemo(
    () => ids.filter(id => !options.some(o => getItemId(o).toString() === id.toString())),
    [ids, options, getItemId],
  );

  // When filterSelectionToCurrentList is true (e.g. contacts by customer), only show ids that are in the current list.
  const effectiveIds = filterSelectionToCurrentList ? idsInList : ids;

  // Fetch full item only for ids that are not in the current list (avoids redundant get-one after selecting from dropdown).
  const selectedQueries = useQueries({
    queries: idsNotInList.map(id => ({
      queryKey: ['select-async-resolved', id],
      queryFn: () => singleQuery.runQuery({ _id: id }),
      enabled: !!id && !filterSelectionToCurrentList,
    })),
  });

  const selectedItems = useMemo(() => {
    const findInOptions = (id: Id): T | undefined =>
      options.find(o => getItemId(o).toString() === id.toString()) as T | undefined;

    if (filterSelectionToCurrentList) {
      // Only show selection for ids that exist in the current list (e.g. contacts for selected customer).
      const items = effectiveIds.map(findInOptions).filter((t): t is T => t !== undefined);
      return items.length === effectiveIds.length ? items : undefined;
    }

    // Resolve each id from list when possible, otherwise from API; preserve order of ids.
    const resolveOne = (id: Id): T | undefined => {
      const fromOptions = findInOptions(id);
      if (fromOptions) return fromOptions;

      const idx = idsNotInList.findIndex(i => i.toString() === id.toString());
      if (idx === -1) return undefined;
      const fromQuery = selectedQueries[idx]?.data as T | undefined;
      return fromQuery;
    };

    const combined = ids.map(resolveOne).filter((t): t is T => t !== undefined);
    return combined.length === ids.length ? combined : undefined;
  }, [
    filterSelectionToCurrentList,
    effectiveIds,
    options,
    getItemId,
    idsNotInList,
    ids,
    selectedQueries,
  ]);

  const selectedItemsLoading =
    !filterSelectionToCurrentList && selectedQueries.some(q => q.isLoading);

  const isSelected = (id: Id): boolean => {
    if (!effectiveIds.length) return false;
    return effectiveIds.some(oid => oid.toString() === id.toString());
  };

  const handleSelect = (item: T) => {
    const id = getItemId(item);
    const currentIds = ids;
    const currentItems = selectedItems ?? [];

    let newIds: Id[];
    let newItems: T[];

    if (mode === 'single') {
      const result = replaceSelectionItem(item, getItemId);
      newIds = result.ids;
      newItems = result.items;
      setOpen(false);
    } else {
      const result = toggleSelectionItem(item, currentIds, currentItems, getItemId);
      newIds = result.ids;
      newItems = result.items;
    }

    const optionWasInCurrent = currentIds.some(oid => oid.toString() === id.toString());
    const optionIsInNew = newIds.some(oid => oid.toString() === id.toString());

    if (mode === 'multi') {
      const isRemovingJustAdded =
        lastAddRef.current &&
        id.toString() === lastAddRef.current.id.toString() &&
        Date.now() - lastAddRef.current.timestamp < DEDUPE_MS &&
        optionWasInCurrent;
      if (isRemovingJustAdded) {
        lastAddRef.current = null;
        return;
      }
      const isAddingJustRemoved =
        lastRemoveRef.current &&
        id.toString() === lastRemoveRef.current.id.toString() &&
        Date.now() - lastRemoveRef.current.timestamp < DEDUPE_MS &&
        !optionWasInCurrent;
      if (isAddingJustRemoved) {
        lastRemoveRef.current = null;
        return;
      }
      if (newIds.length > 0 && !optionWasInCurrent) {
        lastAddRef.current = { id, timestamp: Date.now() };
      } else {
        lastAddRef.current = null;
      }
      if (optionWasInCurrent && !optionIsInNew) {
        lastRemoveRef.current = { id, timestamp: Date.now() };
      } else {
        lastRemoveRef.current = null;
      }
    }

    onChangeSelect(newItems.length > 0 ? newItems : undefined);
  };

  useEffect(() => {
    if (!open || !listContainerRef.current) return;

    const observer = new IntersectionObserver(
      entries => {
        const target = entries[0];
        if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
          onLoadMore();
        }
      },
      {
        root: listContainerRef.current,
        rootMargin: '100px',
        threshold: 0.1,
      },
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [
    open,
    listRefReady,
    hasNextPage,
    isFetchingNextPage,
    onLoadMore,
    loadMoreRef,
    listContainerRef,
  ]);

  return {
    open,
    setOpen,
    searchInputValue: search.inputSearch,
    onSearchInputChange: search.onInputSearchChange,
    clearSearch: search.clearSearch,
    options,
    onLoadMore,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    selectedItems,
    selectedItemsLoading,
    handleSelect,
    isSelected,
    normalizedValue: ids,
    mode,
    loadMoreRef,
    listContainerRef,
    setListRefReady,
    isSearching: isLoading, // this might needs to be changed to isSearching
  };
}
